import { TranslatedString } from "@gnu-taler/taler-util";
import { Fragment, h, VNode } from "preact";
import { useEffect, useId, useMemo, useRef, useState } from "preact/hooks";
import {
  getValueFromPath,
  RecursivePartial,
  useForm,
} from "../../hooks/useForm.js";
import { useTranslationContext } from "../../context/translation.js";
import { RenderAllFieldsByUiConfig } from "../forms-ui.js";
import { resolveFieldHandler, UIFormProps } from "../FormProvider.js";
import { UIFormElementConfig } from "../forms-types.js";
import { convertFormConfigToUiField } from "../forms-utils.js";
import { LabelWithTooltipMaybeRequired } from "./InputLine.js";

type FormType = {};
type Editor = { type: "add" } | { type: "edit"; index: number };
const EMPTY_FORM = {};

function ArrayForm({
  fields,
  selected,
  onClose,
  onRemove,
  onConfirm,
  isNew,
  showTopBorder,
}: {
  fields: UIFormElementConfig[];
  selected: Record<string, unknown> | undefined;
  onClose: () => void;
  onRemove: () => void;
  onConfirm: (r: RecursivePartial<FormType>) => void;
  isNew: boolean;
  showTopBorder: boolean;
}): VNode {
  const { i18n } = useTranslationContext();
  const design = useMemo(
    () => ({ type: "single-column", fields }) as const,
    [fields],
  );
  const form = useForm<FormType>(design, selected ?? EMPTY_FORM);
  const editorRef = useRef<HTMLDivElement>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  useEffect(() => {
    editorRef.current
      ?.querySelector<HTMLElement>(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])",
      )
      ?.focus();
  }, []);

  return (
    <div
      ref={editorRef}
      class={
        showTopBorder
          ? "border-t border-gray-200 px-3 py-4 sm:px-4"
          : "px-3 py-4 sm:px-4"
      }
    >
      <div class="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
        <RenderAllFieldsByUiConfig
          fields={convertFormConfigToUiField(i18n, "root", fields, form.model)}
        />
      </div>

      {confirmingRemoval ? (
        <div
          class="mt-5 rounded-md border border-red-200 bg-red-50 p-3"
          role="alert"
        >
          <p class="text-sm font-medium text-red-800">
            <i18n.Translate>Delete this item?</i18n.Translate>
          </p>
          <div class="mt-3 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirmingRemoval(false)}
              class="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <i18n.Translate>Keep item</i18n.Translate>
            </button>
            <button
              type="button"
              onClick={onRemove}
              class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            >
              <i18n.Translate>Delete item</i18n.Translate>
            </button>
          </div>
        </div>
      ) : (
        <div class="mt-5 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <i18n.Translate>Cancel</i18n.Translate>
          </button>

          {!isNew && (
            <button
              type="button"
              onClick={() => setConfirmingRemoval(true)}
              class="rounded-md px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            >
              <i18n.Translate>Delete</i18n.Translate>
            </button>
          )}

          <button
            type="button"
            disabled={form.status.status !== "ok"}
            onClick={() => onConfirm(form.status.result)}
            class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:shadow-none"
          >
            {isNew ? (
              <i18n.Translate>Add item</i18n.Translate>
            ) : (
              <i18n.Translate>Save changes</i18n.Translate>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function InputArray(
  props: {
    fields: UIFormElementConfig[];
    labelField: string;
  } & UIFormProps<string[]>,
): VNode {
  const { fields, labelField, label, required, tooltip, hidden, help } = props;
  const { i18n } = useTranslationContext();

  const { value, onChange, error } = resolveFieldHandler(
    props.handler,
    props.name,
  );
  const [dirty, setDirty] = useState(false);
  const [editor, setEditor] = useState<Editor | undefined>();
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const generatedId = useId();
  const fieldId = `array-${generatedId}`;
  const labelId = `${fieldId}-label`;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;

  const list = (value ?? []) as unknown as Array<Record<string, unknown>>;

  useEffect(() => {
    if (editor?.type === "edit" && editor.index >= list.length) {
      setEditor(undefined);
    }
  }, [editor, list.length]);

  if (hidden) {
    return <Fragment />;
  }

  const describedBy = [
    help ? helpId : undefined,
    dirty && error ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  function closeEditor(): void {
    const restoreAddFocus = editor?.type === "add";
    setDirty(true);
    setEditor(undefined);
    setTimeout(() => {
      (restoreAddFocus
        ? addButtonRef.current
        : returnFocusRef.current
      )?.focus();
    }, 0);
  }

  function labelForItem(item: Record<string, unknown>, index: number) {
    const labelValue = getValueFromPath(item, labelField.split("."));
    if (Array.isArray(labelValue)) {
      return labelValue.length > 0
        ? labelValue.join(", ")
        : i18n.str`Item ${index + 1}`;
    }
    if (labelValue === undefined || labelValue === null || labelValue === "") {
      return i18n.str`Item ${index + 1}`;
    }
    return String(labelValue) as TranslatedString;
  }

  function renderEditor(selected: Record<string, unknown> | undefined) {
    if (!editor || props.disabled) return undefined;
    const isNew = editor.type === "add";
    return (
      <ArrayForm
        key={isNew ? "add" : `edit-${editor.index}`}
        fields={fields}
        isNew={isNew}
        showTopBorder={isNew ? list.length > 0 : true}
        selected={selected}
        onRemove={() => {
          if (editor.type !== "edit") return;
          const newValue = [...list];
          newValue.splice(editor.index, 1);
          onChange(newValue as any);
          closeEditor();
        }}
        onClose={closeEditor}
        onConfirm={(newItem) => {
          const newValue = [...list];
          if (editor.type === "add") {
            newValue.push(newItem as Record<string, unknown>);
          } else {
            newValue.splice(
              editor.index,
              1,
              newItem as Record<string, unknown>,
            );
          }
          onChange(newValue as any);
          closeEditor();
        }}
      />
    );
  }

  return (
    <div class="sm:col-span-6">
      <LabelWithTooltipMaybeRequired
        label={label}
        technicalName={props.technicalName}
        required={required}
        tooltip={tooltip}
        labelId={labelId}
      />
      {help && (
        <p class="mt-2 text-sm text-gray-500" id={helpId}>
          {help}
        </p>
      )}
      {dirty && error && (
        <p class="mt-2 text-sm text-red-600" id={errorId} role="alert">
          {error}
        </p>
      )}

      <div
        class="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white"
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy || undefined}
      >
        {list.length > 0 && (
          <ul class="divide-y divide-gray-200">
            {list.map((item, index) => {
              const itemLabel = labelForItem(item, index);
              const isEditing =
                editor?.type === "edit" && editor.index === index;
              return (
                <li key={index} class={isEditing ? "bg-indigo-50/50" : ""}>
                  <div class="flex min-h-14 items-center justify-between gap-3 px-3 py-2 sm:px-4">
                    <span class="min-w-0 break-words text-sm font-medium text-gray-900">
                      {itemLabel}
                    </span>
                    {!props.disabled && (
                      <button
                        type="button"
                        disabled={editor !== undefined}
                        aria-label={i18n.str`Edit ${itemLabel}`}
                        onClick={(event) => {
                          returnFocusRef.current = event.currentTarget;
                          setEditor({ type: "edit", index });
                        }}
                        class="shrink-0 rounded-md px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <i18n.Translate>Edit</i18n.Translate>
                      </button>
                    )}
                  </div>
                  {isEditing ? renderEditor(item) : undefined}
                </li>
              );
            })}
          </ul>
        )}

        {!props.disabled &&
          (editor?.type === "add" ? (
            renderEditor(undefined)
          ) : (
            <div
              class={list.length > 0 ? "border-t border-gray-200 p-3" : "p-3"}
            >
              <button
                ref={addButtonRef}
                id={`${fieldId}-add`}
                type="button"
                disabled={editor !== undefined}
                onClick={() => setEditor({ type: "add" })}
                class="w-full rounded-md border border-dashed border-gray-300 px-3 py-2.5 text-sm font-semibold text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
              >
                <i18n.Translate>Add item</i18n.Translate>
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}
